import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { setError } from '../../../actions';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { AgencyPageSchema } from './AgencyPageSchema';
import { InteractionTargetTypeEnum } from '../../../graphql/types';
import { getSelectedAgencyQuery } from '../../../api/queries';
import {
  AgencyContent,
  AgencyItemsWrapper,
  AgencyLayout,
  BottomPhoneWrapper,
  HeaderWrapper
} from './styled';
import { WhiteHeader } from '../../WhiteHeader';
import { SelectedAgencyQuery } from '../../../graphql/graphql';
import { AgencyInfoBlock } from '../../Agency/AgencyInfoBlock';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';
import { AgencyFilterDealTypeEnum } from '../../../utils/constants/agencyPage';
import { AgencyFilter } from '../../Agency/AgencyFilter';
import { buildAgencySeoContent } from '../../../utils/helpers/agencyPage';
import { Language } from '../../../utils/types';
import { useOrganizationHit } from '../../../hooks/useOrganizationHit';

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

interface AgencyPageProps {
  agencySlug: string;
  hasError?: boolean;
  currentLang: Language;
  initialFilter?: {
    dealType: AgencyFilterDealTypeEnum | null;
    category: string | null;
  };
}

export const AgencyPage: React.FC<AgencyPageProps> = ({
  agencySlug,
  hasError = false,
  currentLang,
  initialFilter
}) => {
  const dispatch = useDispatch();
  const { isSmallOrLargeDesktop } = useScreenSize();
  const isClient = useClient();
  const router = useRouter();

  const { data, error } = useQuery<SelectedAgencyQuery>(
    getSelectedAgencyQuery,
    {
      variables: { slug: agencySlug }
    }
  );

  const agencyData = data?.agency;
  const categories = data?.categories;

  useOrganizationHit({
    id: agencyData?.id,
    targetType: InteractionTargetTypeEnum.Agency
  });

  useEffect(() => {
    if (hasError || error) dispatch(setError(true));
  }, [hasError, dispatch, error]);

  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    useFullPhoneNumbers({
      id: agencySlug,
      residenceId: null,
      path: 'agentlikler',
      targetType: InteractionTargetTypeEnum.Agency,
      agencyId: agencyData?.id
    });

  const segments = ([] as string[]).concat(router.query.segments ?? []);
  const { title, description, h1 } = buildAgencySeoContent(
    agencyData?.name ?? null,
    currentLang,
    segments
  );

  if (!agencyData) return null;

  const isShowFilters = !!agencyData.itemsCount;

  return (
    <>
      {title && description && (
        <NextSeo title={title} description={description} />
      )}
      {agencyData && <AgencyPageSchema agencyData={agencyData} />}
      <HeaderWrapper>
        <WhiteHeader isShowBackButton />
      </HeaderWrapper>
      <AgencyLayout>
        <AgencyContent>
          <AgencyInfoBlock
            agencyData={agencyData}
            onClickHandler={onClickHandler}
            phonesShown={phonesShown}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            h1={h1}
          />
          {isShowFilters && (
            <AgencyItemsWrapper>
              <AgencyFilter
                categories={agencyData.itemCategories}
                allCategories={categories}
                agencyId={agencyData.id}
                initialFilter={initialFilter}
              />
            </AgencyItemsWrapper>
          )}
        </AgencyContent>
      </AgencyLayout>
      {(!isSmallOrLargeDesktop || !isClient) && (
        <BottomPhoneWrapper>
          <BottomPhoneButton
            phones={phonesShown}
            clickHandler={onClickHandler}
            stat="agency-phones-button"
            {...{ setIsVisible, isVisible }}
          />
        </BottomPhoneWrapper>
      )}
    </>
  );
};
